import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { SectionTitle } from "@sprout/typography.tsx";
import JobRecommendationCard, {
  type JobRecommendation,
} from "@components/dashboard/job_recommendation_card.tsx";
import useTranslation from "@hooks/use_translation.ts";
import { useSafePostHog } from "@hooks/use_posthog.ts";
import useRecommendations, { recommendationsQueryKey } from "@/queries/use_recommendations.ts";
import useRecommendationFeedback from "@/queries/use_recommendation_feedback.ts";

interface Props {
  appliedJobPostId: string;
}

export const ConfirmationRecommendations = ({ appliedJobPostId }: Props) => {
  const { t } = useTranslation("jobSearch");
  const posthog = useSafePostHog();
  const queryClient = useQueryClient();
  const submitFeedback = useRecommendationFeedback("application_confirmation");

  const [arrivedInTime] = useState(
    () => queryClient.getQueryData(recommendationsQueryKey(appliedJobPostId)) !== undefined,
  );

  const { data: recommendations = [] } = useRecommendations(appliedJobPostId, {
    enabled: arrivedInTime,
  });

  if (!arrivedInTime || recommendations.length === 0) return null;

  const handleTrackJob = (job: JobRecommendation) => {
    posthog?.capture("card_clicked", {
      card_type: "recommendation",
      source: "application_confirmation",
      job_post_id: job.id,
      job_title: job.title,
      company_name: job.companyName,
      previously_viewed: job.viewed,
    });
  };

  return (
    <div className="flex flex-col items-start gap-3 w-full">
      <SectionTitle>
        {t("applicationSuccess.subHeaderForRecommendations", { count: recommendations.length })}
      </SectionTitle>
      <div className="flex flex-col gap-3 w-full">
        {recommendations.map((job) => (
          <JobRecommendationCard
            key={job.id}
            job={job}
            compact
            onViewJob={() => handleTrackJob(job)}
            feedback={job.feedback}
            onFeedback={(next) => submitFeedback(job, next)}
          />
        ))}
      </div>
    </div>
  );
};

export default ConfirmationRecommendations;
